/**
 * Utility functions and React hook for search bar inline ghost autocomplete.
 */
import { useMemo, useCallback } from "react";

/**
 * Finds the first item in the list matching the search query prefix and returns the complete suggestion string.
 *
 * @param {string} searchQuery - Current text in search input.
 * @param {Array} items - List of items/products to search within.
 * @param {Function|string} [getItemName] - Function or property name to extract item name. Defaults to item.name || item.title || String(item).
 * @returns {string} Suggested full text, or empty string if no match.
 */
export function getInlineSuggestion(searchQuery, items = [], getItemName) {
  if (!searchQuery || typeof searchQuery !== "string") return "";

  const trimmedQuery = searchQuery.trimStart();
  if (!trimmedQuery) return "";

  const queryLower = trimmedQuery.toLowerCase();

  const getName = (item) => {
    if (!item) return "";
    if (typeof getItemName === "function") return getItemName(item) || "";
    if (typeof getItemName === "string") return item[getItemName] || "";
    return item.name || item.title || (typeof item === "string" ? item : "");
  };

  const match = items.find((item) => {
    const name = getName(item);
    return name && name.toLowerCase().startsWith(queryLower);
  });

  if (!match) return "";

  const matchName = getName(match);
  // Match prefix casing with user input, append remainder of matching name
  return searchQuery + matchName.slice(trimmedQuery.length);
}

/**
 * Intercepts Tab and Right Arrow key events to apply inline suggestion if present.
 *
 * @param {SyntheticEvent} event - KeyDown event.
 * @param {Object} config - Config containing searchQuery, inlineSuggestion, setQuery callback.
 * @returns {boolean} True if autocomplete tab was handled, false otherwise.
 */
export function handleAutocompleteKeyDown(event, { searchQuery, inlineSuggestion, setQuery }) {
  if (!inlineSuggestion || !setQuery) return false;

  const isTabKey = event.key === "Tab";
  const isRightArrowAtEnd =
    event.key === "ArrowRight" &&
    event.target &&
    event.target.selectionStart === searchQuery.length;

  if (isTabKey || isRightArrowAtEnd) {
    if (inlineSuggestion.toLowerCase() !== searchQuery.toLowerCase()) {
      event.preventDefault();
      setQuery(inlineSuggestion);
      return true;
    }
  }

  return false;
}

/**
 * Custom hook to manage inline search autocomplete state and key handlers.
 *
 * @param {string} searchQuery - Active search text.
 * @param {Array} items - Products or item list.
 * @param {Function} setQuery - State setter for search query.
 * @param {Function|string} [getItemName] - Item name accessor.
 */
export function useInlineAutocomplete(searchQuery, items, setQuery, getItemName) {
  const inlineSuggestion = useMemo(() => {
    return getInlineSuggestion(searchQuery, items, getItemName);
  }, [searchQuery, items, getItemName]);

  const onKeyDown = useCallback(
    (e, fallbackKeyDown) => {
      const handled = handleAutocompleteKeyDown(e, {
        searchQuery,
        inlineSuggestion,
        setQuery,
      });

      if (!handled && typeof fallbackKeyDown === "function") {
        fallbackKeyDown(e);
      }
    },
    [searchQuery, inlineSuggestion, setQuery]
  );

  return {
    inlineSuggestion,
    onKeyDown,
  };
}
